<!-- app/components/Header/MainMenu.vue -->
<template>
  <nav :class="classesNav">
    <template v-for="(item, itemKey) in validItems" :key="itemKey">

      <!-- Item simples (sem filhos) -->
      <UButton v-if="!item.children" v-bind="item" variant="ghost" :class="classesItem" active-class="font-semibold" @click="uiStore.openSlideoverMainMenu = false" />

      <!-- Item com submenu -->
      <UPopover v-else mode="hover" :open-delay="100" :close-delay="150"
                :content="{ align: 'start', side: 'bottom' }" :ui="{ content: 'mr-18'}">
        <UButton :label="item.label" variant="ghost" icon="ph:caret-down-bold" trailing :class="[...classesItem, 'btn-rotate-icon-hover']" />
        <template #content>
          <div :class="classesSubmenuPanel">
            <UButton v-for="(child, childKey) in item.children" :key="childKey" v-bind="child" variant="ghost"
                     class="text-sm sm:text-base" :class="classesSubmenuItem" @click="uiStore.openSlideoverMainMenu = false" />
          </div>
        </template>
      </UPopover>

    </template>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import type { NavigationMenuItem } from '@nuxt/ui'
import { createSlug } from '~/utils/strings'

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type AppMenuItem = Omit<NavigationMenuItem, 'type'> & {
  type?: 'button' | 'submit' | 'reset'
  children?: AppMenuItem[]
}

// ─── Props ────────────────────────────────────────────────────────────────────

const props = withDefaults(
  defineProps<{
    variant: 'primaria' | 'secundaria'
    orientation?: 'horizontal' | 'vertical'
  }>(),
  { orientation: 'horizontal' }
)

// ─── Stores ───────────────────────────────────────────────────────────────────

const userStore = useUserStore()
const uiStore = useUiStore()
const useCatalogo = useCatalogStore()

// ─── Helpers de estado ────────────────────────────────────────────────────────

const isPrimaria = computed(() => props.variant === 'primaria')
const isVertical = computed(() => props.orientation === 'vertical')

// ─── Classes computadas ───────────────────────────────────────────────────────

/**
 * Nav raiz: alterna entre linha (header desktop) e coluna (slideover mobile).
 * No modo vertical, ocupa toda a largura para os botões se expandirem.
 */
const classesNav = computed(() => [
  'flex gap-1',
  isVertical.value ? 'flex-col w-full' : 'flex-row items-center',
])

/**
 * Botão de cada item do menu.
 */
const classesItem = computed(() => [
  'uppercase text-base text-nowrap transition-colors',
  isVertical.value ? 'w-full justify-start' : '',
  isPrimaria.value
    ? 'text-white/90 hover:text-white hover:bg-white/10 active:bg-white/20'
    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
])

/**
 * Painel do submenu (dropdown ou flyout lateral no modo vertical).
 * No modo primária o painel herda o degradê para manter consistência visual
 * quando aberto dentro do Slideover.
 */
const classesSubmenuPanel = computed(() => [
  'flex flex-col min-w-75 p-2 rounded-xl shadow-xl',
  isVertical.value
    ? 'bg-gradient-to-b from-[#039B75] to-[#01365A] ring-1 ring-white/20'
    : 'bg-white ring-1 ring-gray-200',
])

/**
 * Botões filhos dentro do submenu.
 */
const classesSubmenuItem = computed(() => [
  'w-full justify-start text-left text-nowrap transition-colors',
  isVertical.value
    ? 'text-white/90 hover:text-white hover:bg-white/10'
    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100',
])

// ─── Itens do menu ────────────────────────────────────────────────────────────

// 1. Categorias agora é computed (reage às mudanças na store automaticamente)
const categorias = computed<AppMenuItem[]>(() => {
  return useCatalogo.getAreas.flatMap((area: Area) =>
    area.categoryId
      ? [{ label: area.name, to: `/categoria/${createSlug(area.name)}`, external: false }]
      : []
  )
})

const itemMyCourses = computed<AppMenuItem | undefined>(() =>
  userStore.myCourses.length > 0
    ? { label: 'Meus Cursos', to: '/my/courses.php', external: true }
    : undefined
)

const itemAdminSite = computed<AppMenuItem | undefined>(() =>
  userStore.isAdmin
    ? { label: 'Administração do Site', to: '/admin/search.php', external: true }
    : undefined
)

// 2. O validItems junta tudo dinamicamente e filtra os undefineds num só passo
const validItems = computed(() => {
  // Itens básicos
  const items: (AppMenuItem | undefined)[] = [
    { label: 'Inicial', to: '/' }
  ];

  // Adiciona "Painel" se estiver logado
  if (userStore.isLoggedIn) {
    items.push({ label: 'Painel', to: '/my/', external: true });
  }

  // Adiciona o resto do menu
  items.push(
    { label: 'Cursos', children: categorias.value },
    { label: 'Validar Certificado', to: '/validar-certificado' },
    itemMyCourses.value,
    itemAdminSite.value,
  );

  // Retorna apenas os válidos
  return items.filter((item): item is AppMenuItem => item !== undefined);
})
</script>